/**
 * Payments — the ledger, plus paying by UPI.
 *
 * The UPI flow is the client's design: the company QR is shown, the customer
 * pays in their own UPI app, then uploads a screenshot. Admin verifies it
 * against the bank.
 *
 * The critical honesty here: **an unverified payment does not reduce the
 * outstanding balance.** It is shown separately and labelled. Anything else
 * would let a retailer believe they had paid while the ledger disagreed — and
 * would let them order against money that never arrived.
 */

import { useEffect, useState } from 'react';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, Empty, ErrorState, Money, MoneyInput, PhotoTile,
  Sheet, Skeleton, TextInput, useToast,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus } from '../../lib/useApi';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { api, ApiError, API_BASE, uploadMedia, openPdf, fetchBlob } from '../../lib/api';
/**
 * previewUrl / revokePreview come from the static camera import below. The
 * image sourcing itself — take a photo, pick from the gallery, or paste a
 * WhatsApp/UPI screenshot from the clipboard (item #10) — now lives in
 * ImageCaptureSheet, so this screen no longer calls pickImage/compress
 * directly. A customer paying their own ledger almost always already has the
 * confirmation screenshot in their gallery, which is exactly what the chooser
 * is for; a camera-only flow was the wrong shape.
 */
import {
  previewUrl, revokePreview,
  type CompressedImage,
} from '../../lib/camera';
import { ImageCaptureSheet } from '../../components/ImageCaptureSheet';
import { dateShort, daysBetween, money } from '../../lib/format';

interface Ledger {
  customer: { name: string; code: string; creditLimitPaise: number; discountPercent: number };
  outstandingPaise: number;
  outstandingDisplay: string;
  creditAvailablePaise: number | null;
  ageing: Record<string, number>;
  pendingVerification: { amountPaise: number; count: number; note: string };
  invoices: {
    id: string; invoice_number: string; invoice_date: string; due_date: string | null;
    net_amount_paise: number; paid_amount_paise: number; balance_amount_paise: number;
    payment_status: string; status: string;
  }[];
  payments: {
    id: string; receipt_number: string; payment_date: string; amount_paise: number;
    payment_mode: string; verification_status: string;
  }[];
  returnablePackaging: { returnable_type: string; quantity_outstanding: number }[];
}

interface UpiConfig {
  configured: boolean;
  upiId?: string;
  payeeName?: string;
  bankName?: string;
  qrSvgUrl?: string;
  instructions?: { en: string; hi: string };
  message?: string;
}

export default function Ledger() {
  const t = useT();
  const toast = useToast();
  const { user } = useAuth();
  const [payOpen, setPayOpen] = useState(false);

  const ledger = useQuery<Ledger>(
    user?.customerId ? `/api/invoices/ledger/${user.customerId}` : null,
    { cacheKey: 'me:ledger', ttlSeconds: 300 },
  );
  useRefreshOnFocus(ledger.refetch);

  const upi = useQuery<UpiConfig>('/api/settings/upi', { cacheKey: 'upi:config', ttlSeconds: 86400 });

  if (ledger.loading && !ledger.data) {
    return <Page title={t('ledger.title')}><Skeleton rows={4} height={80} /></Page>;
  }
  if (ledger.error && !ledger.data) {
    return (
      <Page title={t('ledger.title')}>
        <ErrorState message={ledger.error.message} onRetry={ledger.refetch} />
      </Page>
    );
  }
  if (!ledger.data) return <Page title={t('ledger.title')}><Empty /></Page>;

  const d = ledger.data;
  const unpaidInvoices = d.invoices.filter(
    (i) => i.status !== 'cancelled' && ['unpaid', 'part_paid', 'overdue'].includes(i.payment_status),
  );

  return (
    <Page title={t('ledger.title')} subtitle={d.customer.code}>
      <div className="stack">
        <Card>
          <div className="stack-2">
            <div className="row-between">
              <span className="t-sm t-muted">{t('ledger.outstanding')}</span>
              <Money paise={d.outstandingPaise} className="t-xl" tone={d.outstandingPaise > 0 ? 'danger' : 'ok'} />
            </div>
            <div className="row-between t-sm">
              <span className="t-muted">{t('ledger.creditLimit')}</span>
              <Money paise={d.customer.creditLimitPaise} />
            </div>
            {d.creditAvailablePaise !== null && (
              <div className="row-between t-sm">
                <span className="t-muted">{t('ledger.available')}</span>
                <Money paise={d.creditAvailablePaise} tone={d.creditAvailablePaise > 0 ? 'ok' : 'danger'} />
              </div>
            )}
          </div>
        </Card>

        {/* Separated deliberately — see the note at the top of this file. */}
        {d.pendingVerification.count > 0 && (
          <Banner tone="info" title={t('ledger.underVerification')}>
            <span className="t-sm">
              {money(d.pendingVerification.amountPaise)} · {d.pendingVerification.count} payment(s)
              <br />{t('ledger.underVerificationHint')}
            </span>
          </Banner>
        )}

        {d.outstandingPaise > 0 && (
          <Button block size="lg" onClick={() => setPayOpen(true)}>{t('ledger.payNow')}</Button>
        )}

        {/* Ageing buckets. A retailer seeing ₹40,000 sitting in "60+" usually
            acts on it; a single outstanding figure hides that entirely. */}
        {d.outstandingPaise > 0 && (
          <Card>
            <div className="stack-2">
              <span className="t-sm t-semi">{t('ledger.ageing')}</span>
              {(['0-15', '16-30', '31-60', '60+'] as const).map((bucket) => {
                const value = d.ageing[bucket] ?? 0;
                if (value === 0) return null;
                const tone = bucket === '60+' ? 'danger' : bucket === '31-60' ? 'warn' : undefined;
                return (
                  <div key={bucket} className="row-between t-sm">
                    <span className="t-muted">{bucket} days</span>
                    <Money paise={value} tone={tone} />
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {d.returnablePackaging.length > 0 && (
          <Card>
            <div className="row-between">
              <div>
                <span className="t-sm t-semi">{t('ledger.crates')}</span>
                {/* No deposit is charged, so this is not part of what they owe. */}
                <div className="t-xs t-muted">Not part of your outstanding</div>
              </div>
              <span className="t-semi">
                {d.returnablePackaging.map((c) => `${c.quantity_outstanding} ${c.returnable_type}`).join(' · ')}
              </span>
            </div>
          </Card>
        )}

        <Card flush>
          <div className="card__header"><h3>{t('ledger.invoices')}</h3></div>
          {unpaidInvoices.length === 0 ? (
            <div className="card__body"><span className="t-sm t-muted">All settled</span></div>
          ) : (
            <div className="list">
              {unpaidInvoices.map((i) => {
                const overdueDays = i.due_date ? daysBetween(i.due_date) : 0;
                return (
                  <div key={i.id} className="list__item list__item--static">
                    <div className="grow">
                      <div className="row" style={{ gap: 8 }}>
                        <span className="t-semi t-sm">{i.invoice_number}</span>
                        {overdueDays > 0 && (
                          <Chip tone="danger">{t('ledger.overdueBy', { days: overdueDays })}</Chip>
                        )}
                        {i.payment_status === 'part_paid' && <Chip tone="warn">Part paid</Chip>}
                      </div>
                      <div className="t-xs t-muted">
                        {dateShort(i.invoice_date)}
                        {i.due_date && ` · ${t('ledger.dueOn', { date: dateShort(i.due_date) })}`}
                      </div>
                    </div>
                    <div className="t-right">
                      <Money paise={i.balance_amount_paise} className="t-sm" />
                      {i.paid_amount_paise > 0 && (
                        <div className="t-xs t-muted">
                          {t('ledger.paid')} {money(i.paid_amount_paise, { compact: true })}
                        </div>
                      )}
                      {/* A button, not a link: the PDF route needs a bearer
                          token that an anchor cannot carry. See openPdf. */}
                      <button
                        type="button"
                        className="t-xs"
                        style={{
                          color: 'var(--info)', background: 'none', border: 0,
                          padding: 0, cursor: 'pointer', textAlign: 'left',
                        }}
                        onClick={() => void openPdf(`/api/invoices/${i.id}/pdf`)}
                      >{t('order.viewInvoice')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {d.payments.length > 0 && (
          <Card flush>
            <div className="card__header"><h3>{t('ledger.receipts')}</h3></div>
            <div className="list">
              {d.payments.slice(0, 15).map((p) => (
                <div key={p.id} className="list__item list__item--static">
                  <div className="grow">
                    <div className="row" style={{ gap: 8 }}>
                      <span className="t-semi t-sm">{p.receipt_number}</span>
                      {p.verification_status === 'pending' && <Chip tone="warn">{t('ledger.underVerification')}</Chip>}
                      {p.verification_status === 'rejected' && <Chip tone="danger">{t('return.rejected')}</Chip>}
                    </div>
                    <div className="t-xs t-muted">
                      {dateShort(p.payment_date)} · {p.payment_mode.replace(/_/g, ' ')}
                    </div>
                  </div>
                  <Money
                    paise={p.amount_paise}
                    className="t-sm"
                    tone={p.verification_status === 'rejected' ? 'muted' : undefined}
                  />
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      <PaySheet
        open={payOpen}
        onClose={() => setPayOpen(false)}
        upi={upi.data ?? null}
        outstandingPaise={d.outstandingPaise}
        invoices={unpaidInvoices.map((i) => ({
          id: i.id, number: i.invoice_number, balancePaise: i.balance_amount_paise,
        }))}
        customerId={user?.customerId ?? ''}
        onDone={() => { setPayOpen(false); void ledger.refetch(); toast.show(t('return.submitted'), 'ok'); }}
      />
    </Page>
  );
}

/**
 * Pay-by-UPI sheet.
 *
 * The QR is generated server-side from the stored UPI ID, with the amount
 * embedded. Embedding the amount removes the most common cause of a
 * part-payment that then needs manual reconciliation — the customer mistyping
 * it.
 */
function PaySheet({
  open, onClose, upi, outstandingPaise, invoices, customerId, onDone,
}: {
  open: boolean;
  onClose: () => void;
  upi: UpiConfig | null;
  outstandingPaise: number;
  invoices: { id: string; number: string; balancePaise: number }[];
  customerId: string;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();

  const [amountPaise, setAmountPaise] = useState<number | null>(outstandingPaise);
  const [utr, setUtr] = useState('');
  const [photo, setPhoto] = useState<CompressedImage | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);

  useEffect(() => { setAmountPaise(outstandingPaise); }, [outstandingPaise, open]);
  useEffect(() => () => { if (preview) revokePreview(preview); }, [preview]);

  // ImageCaptureSheet does the sourcing and the compression; we just keep the
  // result and its preview URL. Revoke the previous preview so we don't leak
  // object URLs when the customer re-attaches.
  function onImage(img: CompressedImage) {
    if (preview) revokePreview(preview);
    setPhoto(img);
    setPreview(previewUrl(img.blob));
  }

  async function submit() {
    if (!amountPaise || amountPaise <= 0) { toast.show(t('ledger.amountPaid'), 'danger'); return; }
    if (!photo && !utr.trim()) {
      toast.show('Upload a screenshot or enter the UPI reference', 'danger');
      return;
    }
    setBusy(true);
    try {
      let proofMediaId: string | undefined;
      if (photo) {
        const uploaded = await uploadMedia({
          blob: photo.blob,
          purpose: 'upi_screenshot',
          originalSizeBytes: photo.originalSizeBytes,
          widthPx: photo.width,
          heightPx: photo.height,
        });
        proofMediaId = uploaded.mediaId;
      }

      // Allocate oldest invoice first, up to the amount paid. Matches how a
      // retailer thinks about clearing dues and keeps ageing meaningful.
      const allocations: { invoiceId: string; amountPaise: number }[] = [];
      let remaining = amountPaise;
      for (const inv of invoices) {
        if (remaining <= 0) break;
        const applied = Math.min(remaining, inv.balancePaise);
        if (applied > 0) { allocations.push({ invoiceId: inv.id, amountPaise: applied }); remaining -= applied; }
      }

      await api.post('/api/payments', {
        customerId,
        amountPaise,
        paymentMode: 'upi',
        upiReference: utr.trim() || undefined,
        proofMediaId,
        allocations: allocations.length ? allocations : undefined,
      });

      setPhoto(null);
      setUtr('');
      onDone();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  if (!upi?.configured) {
    return (
      <Sheet open={open} onClose={onClose} title={t('ledger.payNow')}>
        <Banner tone="info">{upi?.message ?? 'UPI payment is not set up yet. Please contact us.'}</Banner>
      </Sheet>
    );
  }

  /**
   * The UPI QR, fetched with the bearer token.
   *
   * This was an `<img src="{API}/api/settings/upi/qr">`. Everything under
   * /api/settings requires authentication and an <img> cannot send an
   * Authorization header — so the QR was a broken image for every customer,
   * in the middle of the payment flow. The same fault as the invoice links,
   * found by looking for the pattern rather than by anyone reporting it.
   *
   * The amount is in the URL, so the QR is refetched when it changes; a QR
   * carrying the wrong amount is worse than none, because the customer pays
   * it.
   */
  const qrPath = `/api/settings/upi/qr?amountPaise=${amountPaise ?? 0}`;
  const [qrSrc, setQrSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !upi.configured) return;
    let objectUrl: string | null = null;
    let cancelled = false;

    void fetchBlob(qrPath)
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        objectUrl = url;
        setQrSrc(url);
      })
      .catch(() => setQrSrc(null));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qrPath, open, upi.configured]);

  return (
    <>
    <Sheet
      open={open}
      onClose={onClose}
      title={t('ledger.payNow')}
      footer={
        <>
          <Button variant="ghost" block onClick={onClose} disabled={busy}>{t('app.cancel')}</Button>
          <Button block busy={busy} onClick={() => void submit()}>{t('ledger.submitPayment')}</Button>
        </>
      }
    >
      <div className="stack-4">
        <MoneyInput
          label={t('ledger.amountPaid')}
          required
          valuePaise={amountPaise}
          onChangePaise={setAmountPaise}
          hint={`${t('ledger.outstanding')}: ${money(outstandingPaise)}`}
        />

        <div className="t-center stack-2">
          <span className="t-sm t-semi">{t('ledger.scanQr')}</span>
          <div
            style={{
              background: '#fff', padding: 12, borderRadius: 12, display: 'inline-block',
              margin: '0 auto', border: '1px solid var(--line)',
            }}
          >
            {qrSrc
              ? <img src={qrSrc} alt="UPI QR code" width={220} height={220} />
              : (
                // A fixed-size placeholder rather than a collapsing box, so
                // the sheet does not jump as the QR arrives.
                <div style={{ width: 220, height: 220, display: 'grid', placeItems: 'center' }}>
                  <span className="t-xs t-muted">{t('app.loading')}</span>
                </div>
              )}
          </div>
          <div className="t-xs t-muted">
            {t('ledger.upiId')}: <strong>{upi.upiId}</strong>
          </div>
          <div className="t-xs t-faint">{upi.payeeName}{upi.bankName && ` · ${upi.bankName}`}</div>
        </div>

        <hr className="divider" />

        <PhotoTile
          label={t('ledger.uploadProof')}
          hint={t('ledger.proofHint')}
          previewUrl={preview}
          onCapture={() => setCaptureOpen(true)}
          onRemove={() => { if (preview) revokePreview(preview); setPhoto(null); setPreview(null); }}
        />

        <TextInput
          label={t('ledger.utr')}
          placeholder="12 digit reference"
          value={utr}
          onChange={(e) => setUtr(e.target.value)}
        />

        <Banner tone="warn">
          <span className="t-xs">{t('ledger.underVerificationHint')}</span>
        </Banner>
      </div>
    </Sheet>
    {captureOpen && (
      <ImageCaptureSheet
        open
        title={t('ledger.uploadProof')}
        compressOptions={{ maxDimension: 1400, maxBytes: 2_000_000 }}
        onCapture={onImage}
        onClose={() => setCaptureOpen(false)}
      />
    )}
    </>
  );
}
